<!--
  Save state, and it is a *state* rather than a toast.

  A transient notification is the wrong shape for "your last forty boxes are not on the
  server": it disappears while the annotator is looking at the stone. So a failed save leaves a
  banner up until it succeeds, keeps retrying with backoff, and offers the work as JSON so it
  can leave the browser by hand if it has to.

  Losing the lock is treated differently on purpose. Retrying would be fighting another
  annotator for an image they are actively working on, and the bulk upsert's submitted list is
  authoritative — winning that fight would delete their boxes. So the loop stops and says who
  holds the image. The "claim again" button is manual and unassisted: the service reclaims a
  stale lock with no flag, so the button succeeds only once the other annotator's claim has
  actually lapsed, and never sooner.
-->
<script lang="ts">
  import { annotator } from '../lib/stores/annotator.svelte';
  import { session } from '../lib/stores/session.svelte';

  let copied = $state(false);
  let draftCopied = $state(false);
  let confirmDiscardOther = $state(false);

  const label = $derived.by(() => {
    switch (session.saveState) {
      case 'saving':
        return 'Saving…';
      case 'saved':
        return 'All work saved';
      case 'dirty':
        return 'Unsaved changes — autosaving';
      case 'retrying':
        return `Save failed — retrying (attempt ${session.retryAttempt})`;
      case 'failed':
        return 'Save failed';
      case 'blocked':
        return 'Saving stopped';
      default:
        return 'No changes';
    }
  });

  const tone = $derived.by(() => {
    if (session.saveState === 'failed' || session.saveState === 'blocked') return 'error';
    if (session.saveState === 'retrying') return 'warn';
    if (session.saveState === 'saved') return 'ok';
    return 'info';
  });

  async function copyUnsaved(): Promise<void> {
    try {
      await navigator.clipboard.writeText(session.unsavedJson());
      copied = true;
      setTimeout(() => (copied = false), 4000);
    } catch {
      copied = false;
    }
  }

  /**
   * The way out for a draft this session may not restore. The payload keeps the original
   * `drafted_by`, so the work leaves the browser still attributed to whoever drew it.
   */
  async function copyDraft(): Promise<void> {
    try {
      await navigator.clipboard.writeText(session.recoverableJson());
      draftCopied = true;
      setTimeout(() => (draftCopied = false), 4000);
    } catch {
      draftCopied = false;
    }
  }
</script>

<div class="status row small">
  <span class="dot {tone}" aria-hidden="true"></span>
  <span>{label}</span>
  {#if session.lastSavedAt && session.saveState === 'saved'}
    <span class="muted">at {new Date(session.lastSavedAt).toLocaleTimeString()}</span>
  {/if}
  {#if session.saveState === 'dirty' || session.saveState === 'retrying' || session.saveState === 'failed'}
    <button class="subtle" onclick={() => void session.retryNow()}>Save now</button>
  {/if}
</div>

{#if session.lockLoss}
  <div class="banner error col">
    <strong>{session.lockLoss.holder} holds this image</strong>
    <p class="small">{session.lockLoss.message}</p>
    <p class="small muted">
      Autosave is stopped. Single-writer locking is deliberate: continuing would replace their
      box set with yours. Your work is still here and is kept in this browser.
      {#if session.lockLoss.expiresAt}
        Their claim goes stale at {new Date(session.lockLoss.expiresAt).toLocaleTimeString()}.
      {/if}
    </p>
    <div class="row wrap">
      <button onclick={() => void session.retryClaim()}>Claim again</button>
      <button class="subtle" onclick={() => void copyUnsaved()}>
        {copied ? 'Copied' : 'Copy my unsaved boxes as JSON'}
      </button>
    </div>
  </div>
{:else if session.saveProblem && (session.saveState === 'failed' || session.saveState === 'retrying')}
  <div class="banner error col">
    <strong>Your work is not saved</strong>
    <p class="small">{session.saveProblem.message}</p>
    <p class="small muted mono">code: {session.saveProblem.code}</p>
    {#if session.saveProblem.code === 'disagreement_conflict'}
      <p class="small">
        A letter is recorded as both boxed and not present. Clear the not-present mark on that
        chip, or delete the box — the two statements contradict and the service will not store
        both.
      </p>
    {/if}
    {#if session.saveProblem.code === 'validation_failed'}
      <ul class="small muted">
        {#each Object.entries(session.saveProblem.context) as [field, detail] (field)}
          <li><span class="mono">{field}</span>: {detail}</li>
        {/each}
      </ul>
    {/if}
    <p class="small muted">
      Nothing has been discarded. The boxes are still on the canvas and in this browser's local
      storage, and will be re-sent.
    </p>
    <div class="row wrap">
      <button class="primary" onclick={() => void session.retryNow()}>Retry now</button>
      <button class="subtle" onclick={() => void copyUnsaved()}>
        {copied ? 'Copied' : 'Copy my unsaved boxes as JSON'}
      </button>
    </div>
  </div>
{/if}

{#if session.rejected.length}
  <div class="banner warn col">
    <strong>{session.rejected.length} box(es) were not stored</strong>
    <ul class="small">
      {#each session.rejected as item, index (index)}
        <li>{item.reason}</li>
      {/each}
    </ul>
  </div>
{/if}

{#if session.anomalies.length}
  <div class="banner warn col">
    <strong>The write path reported something</strong>
    <ul class="small">
      {#each session.anomalies as anomaly, index (index)}
        <li>
          <span class="mono">{anomaly.kind}</span>
          {#if anomaly.reasons.length}— {anomaly.reasons.join('; ')}{/if}
        </li>
      {/each}
    </ul>
    <p class="small muted">
      A clamped box usually means a coordinate bug rather than a careless drag. It is recorded
      in the service's anomaly table as well as shown here.
    </p>
  </div>
{/if}

{#if session.strandedWork}
  <div class="banner error col">
    <strong>Unsaved boxes could not be stored anywhere</strong>
    <p class="small">
      The previous image has boxes that reached neither the service nor this browser's local
      storage (its quota is probably full). Loading another image over the top of them is
      refused, because that is the one action that would lose them. Copy them out, then reload.
    </p>
    <button class="primary" onclick={() => void copyUnsaved()}>
      {copied ? 'Copied' : 'Copy my unsaved boxes as JSON'}
    </button>
  </div>
{/if}

{#if session.recoverable}
  <div class="banner info col">
    <strong>Unsaved work recovered</strong>
    {#if session.recoverable.boxes.length === 0}
      <!--
        An empty draft is not nothing. It records that every box on this image was deleted and
        that the deletion never reached the service — and the bulk upsert's submitted list is
        authoritative, so restoring it deletes them again. Saying "0 box(es)" would read as a
        bug rather than as the statement it is.
      -->
      <p class="small">
        A draft from {new Date(session.recoverable.savedAt).toLocaleString()} records this image
        with <strong>no boxes</strong>. If the service still holds boxes for it, restoring this
        draft deletes them — that is what the draft says was done and never saved.
      </p>
    {:else}
      <p class="small">
        This browser has {session.recoverable.boxes.length} box(es) for this image from
        {new Date(session.recoverable.savedAt).toLocaleString()} that never reached the service.
      </p>
    {/if}
    {#if session.recoverableStale}
      <!--
        Offered, not deleted. An old draft the annotator can see and discard costs one click; a
        deleted one costs the work. `session.#offerDraft` says why at length.
      -->
      <p class="small warn-text">
        It is more than a fortnight old, or its timestamp cannot be read. It is still offered —
        nothing deletes an unsaved draft — but check the boxes against the stone before
        completing the image.
      </p>
    {/if}
    {#if session.recoverableOwner === 'other'}
      <!--
        Refused, not warned about. `annotator_id` is sent per request, so restoring here would
        write another person's boxes into the database as this session's — and provenance is
        non-nullable precisely so that never happens. Nothing is deleted: the draft stays in
        this browser for its owner, and the JSON button gets it out by hand.
      -->
      <p class="small warn-text">
        They were drafted by <strong class="mono">{session.recoverable.annotatorId}</strong>, not
        by you (<span class="mono">{annotator.id || 'no id set'}</span>), so this session cannot
        restore them: every box records who drew it, and saving these here would attribute their
        work to you for good. Set this session's annotator id to
        <span class="mono">{session.recoverable.annotatorId}</span> to recover them, or copy the
        draft out and hand it back.
      </p>
    {:else if session.recoverableOwner === 'unattributed'}
      <p class="small warn-text">
        The draft carries no annotator id, so it was written before one was set. Restoring it
        will attribute the boxes to <span class="mono">{annotator.id || 'nobody'}</span>.
      </p>
    {/if}
    <div class="row wrap">
      {#if session.recoverableOwner === 'other'}
        <button class="primary" onclick={() => void copyDraft()}>
          {draftCopied ? 'Copied' : 'Copy their draft as JSON'}
        </button>
        <button class="subtle" onclick={() => annotator.requestIdentity('edit')}>
          Change annotator id
        </button>
        {#if confirmDiscardOther}
          <button
            class="subtle"
            onclick={() => {
              session.discardRecoverable();
              confirmDiscardOther = false;
            }}
          >
            Delete {session.recoverable.annotatorId}'s draft — confirm
          </button>
        {:else}
          <button class="subtle" onclick={() => (confirmDiscardOther = true)}>Discard</button>
        {/if}
      {:else}
        <button class="primary" onclick={() => session.restoreRecoverable()}>Restore them</button>
        <button class="subtle" onclick={() => session.discardRecoverable()}>Discard</button>
      {/if}
    </div>
  </div>
{/if}

<style>
  .status {
    gap: 8px;
  }

  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--text-dim);
    flex: none;
  }

  .dot.ok {
    background: var(--ok);
  }
  .dot.warn {
    background: var(--warn);
  }
  .dot.error {
    background: var(--danger);
  }
  .dot.info {
    background: var(--accent);
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  .warn-text {
    color: var(--warn);
  }

  p {
    margin: 0;
  }
</style>
